import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { useCanSeeCost } from '~/hooks/useCanSeeCost';

import {
  fetchCostActual,
  fetchCostEstimate,
  fetchCostEstimates,
  fetchCostHistory,
  submitCostDecision,
} from '../services/cost-estimate.service';
import type {
  CostDecisionRequest,
  CostGate,
  CostEstimate,
  CostHistoryParams,
} from '../types/cost-estimate.types';

const POLL_MS = 15_000;
/** Waiting for the gate to record an estimate while the run is live: the run poll's `costGate`
 *  (where the backend sends it) invalidates sooner, so this is the fallback cadence. */
const AWAITING_POLL_MS = 15_000;
const APPROVALS_POLL_MS = 30_000;
/** Actual spend accrues as products finish; a minute is fresh enough for a running total. */
const ACTUAL_POLL_MS = 60_000;

interface UseCostEstimateOptions {
  /**
   * Whether the host run is live (not terminal). `true`: poll while no estimate exists yet or
   * it is pending. `false`: the run ended — stop polling (one last fetch on the transition
   * picks up the final status). `undefined`: the host does not know — poll only while pending.
   */
  runActive?: boolean | undefined;
}

/**
 * Latest cost estimate for a run (null = none recorded). Polls while the run is live and the
 * estimate is missing or PENDING_APPROVAL (or, when the host does not report the run state,
 * while pending); stops once decided or once the run ends. Deliberately independent of stage
 * heuristics — a host with stale or missing stages must still see the estimate land.
 */
export function useCostEstimate(
  workflowId: string | null | undefined,
  { runActive }: UseCostEstimateOptions = {},
) {
  const query = useQuery({
    queryKey: queryKeys.costEstimates.byWorkflow(workflowId ?? ''),
    queryFn: () => fetchCostEstimate(workflowId ?? ''),
    enabled: !!workflowId,
    staleTime: 10_000,
    // Only an undecided gate is worth refreshing on tab focus; everything else changes rarely.
    refetchOnWindowFocus: (q) =>
      runActive !== false &&
      (q.state.data?.status === 'PENDING_APPROVAL' || (!q.state.data && runActive === true)),
    refetchInterval: (q) => {
      if (runActive === false) return false;
      const data = q.state.data;
      if (data?.status === 'PENDING_APPROVAL') return POLL_MS;
      if (!data && runActive === true) return AWAITING_POLL_MS;
      return false;
    },
  });

  // Live → ended: one last fetch so a CANCELLED / decided status lands after polling stops.
  const { refetch } = query;
  const wasActive = useRef(runActive);
  useEffect(() => {
    if (wasActive.current === true && runActive === false && workflowId) void refetch();
    wasActive.current = runActive;
  }, [runActive, workflowId, refetch]);

  return query;
}

/**
 * Keeps a run's estimate in step with the run poll's `costGate`: when the gate appears,
 * clears, or moves to another estimate, the estimate query for the host's id (and the gate's
 * own workflow id) is invalidated so the card and banner update without waiting for their
 * own poll. An absent `costGate` (backend without the field) never triggers anything.
 */
export function useCostGateSync(
  workflowId: string | null | undefined,
  costGate: CostGate | null | undefined,
) {
  const qc = useQueryClient();
  const gateKey = costGate === undefined ? undefined : (costGate?.estimateId ?? null);
  const previous = useRef({
    key: gateKey,
    gateWorkflowId: costGate?.workflowId ?? null,
  });
  useEffect(() => {
    const prev = previous.current;
    previous.current = { key: gateKey, gateWorkflowId: costGate?.workflowId ?? null };
    // Unknown on either side (field absent, or first answer) is not a change.
    if (gateKey === undefined || prev.key === undefined || prev.key === gateKey) return;
    const ids = new Set(
      [workflowId, costGate?.workflowId, prev.gateWorkflowId].filter((id): id is string => !!id),
    );
    for (const id of ids) {
      void qc.invalidateQueries({ queryKey: queryKeys.costEstimates.byWorkflow(id) });
    }
  }, [gateKey, costGate?.workflowId, workflowId, qc]);
}

interface UseCostActualOptions {
  /** Fetch at all — hosts pass true once the run's enrichment has started. */
  enabled?: boolean;
  /** Poll every 60s while true (enrichment running). */
  poll?: boolean;
}

/**
 * Actual LLM spend for a run (null = nothing recorded yet). Polls while `poll`; when
 * `poll` flips off (enrichment finished) it refetches once so the final total lands.
 * Never requested for customers: the endpoint is lily.ai-only (403).
 */
export function useCostActual(
  workflowId: string | null | undefined,
  { enabled = true, poll = false }: UseCostActualOptions = {},
) {
  const canSeeCost = useCanSeeCost();
  const isEnabled = enabled && canSeeCost && !!workflowId;
  const query = useQuery({
    queryKey: queryKeys.costEstimates.actual(workflowId ?? ''),
    queryFn: () => fetchCostActual(workflowId ?? ''),
    enabled: isEnabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: poll && isEnabled ? ACTUAL_POLL_MS : false,
  });

  // Running → done: one last fetch picks up the spend recorded since the previous poll.
  const { refetch } = query;
  const wasPolling = useRef(poll);
  useEffect(() => {
    if (wasPolling.current && !poll && isEnabled) void refetch();
    wasPolling.current = poll;
  }, [poll, isEnabled, refetch]);

  return query;
}

/** Approve / reject a pending estimate. Errors are left to the caller (403/409
 *  get their own copy); the estimate is refetched whatever the outcome. */
export function useCostDecision(workflowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CostDecisionRequest) => submitCostDecision(workflowId, body),
    onSuccess: (estimate) => {
      // A host may read the estimate by its own run id (e.g. a UI pipeline's job id) that the
      // backend resolves to a child workflow — write both keys so neither shows it pending.
      qc.setQueryData(queryKeys.costEstimates.byWorkflow(workflowId), estimate);
      qc.setQueryData(queryKeys.costEstimates.byWorkflow(estimate.workflowId), estimate);
    },
    onSettled: () => {
      // Prefix match: refreshes this run's estimate and every pending-approvals list.
      void qc.invalidateQueries({ queryKey: queryKeys.costEstimates.all });
    },
  });
}

/** Approver-tier estimates awaiting a decision; polls every 30s while `enabled`. */
export function usePendingApproverEstimates({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CostEstimate[]>({
    queryKey: queryKeys.costEstimates.list('PENDING_APPROVAL', 'APPROVERS'),
    queryFn: () => fetchCostEstimates({ status: 'PENDING_APPROVAL', tier: 'APPROVERS' }),
    enabled,
    staleTime: 10_000,
    refetchInterval: APPROVALS_POLL_MS,
  });
}

/** One page of decided estimates for the History tab; keeps the previous page while paging. */
export function useCostHistory(
  params: CostHistoryParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.costEstimates.history({ ...params }),
    queryFn: () => fetchCostHistory(params),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}
